import React, { useEffect, useMemo, useRef, useState } from "react";

import { checkFeatureFlag, numberRangeRule, useSilverVersionsPublic, useSoilSightCurrency, useTranslation } from "utils";

import { MACHINERY_DIESEL_LIMITS, SOIL_SIGHT_DRAWER_WIDTH, TREND_PCT_LIMITS } from "consts";

import { FieldListField, SoilSightNonCropDefaults, versionOptionLabel } from "models";

import { Button, Drawer, FieldMapIcon, Form, Icon, InputNumber, KlimRadioGroup, Option, RadioChangeEvent, Select, Slider, Typography } from "components";

import { FieldUpload } from "pages/app/fields/components/FieldUpload";
import { GuidancePanel } from "pages/app/scenario-analysis/components/GuidancePanel";
import { HelpFieldWrapper } from "pages/app/scenario-analysis/components/HelpFieldWrapper";
import { ProjectSettings, ProjectSettingsHelpFieldKey } from "pages/app/scenario-analysis/types/scenario";
import { formatSignedPercent } from "pages/app/scenario-analysis/utils/comparisonFormatting";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ProjectSettingsDrawerProps {
  visible: boolean;
  onClose: () => void;
  settings: ProjectSettings;
  /** `shownSettings` is what the form showed. We fill in the diesel and field defaults where nothing is saved. */
  onSave: (settings: ProjectSettings, shownSettings: ProjectSettings) => void;
  nonCropDefaults?: SoilSightNonCropDefaults | null;
  fields?: FieldListField[];
  onFieldAdded?: () => void;
}

/** Only these fields have guidance copy; the rest show the label alone. */
const FIELDS_WITH_GUIDANCE: ReadonlySet<ProjectSettingsHelpFieldKey> = new Set(["marketPriceTrend", "costDevelopment", "fieldSelect", "machineryDiesel"]);

const DEFAULT_VERSION = "__default__";

const getMarketPriceClass = (val: number) => {
  if (val > 0) return "is-positive";
  if (val < 0) return "is-negative";
  return "is-neutral";
};

const getCostDevClass = (val: number) => {
  if (val > 0) return "is-negative";
  if (val < 0) return "is-positive";
  return "is-neutral";
};

// ─── Main component ───────────────────────────────────────────────────────────

export const ProjectSettingsDrawer = ({ visible, onClose, settings, onSave, nonCropDefaults, fields = [], onFieldAdded }: ProjectSettingsDrawerProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { currencySymbol } = useSoilSightCurrency();
  const [form] = Form.useForm();
  const [focusedField, setFocusedField] = useState<ProjectSettingsHelpFieldKey | null>("marketPriceTrend");
  const [climateImpact, setClimateImpact] = useState<boolean>(settings.modelSettings.climateImpact);
  const [rothCModel, setRothCModel] = useState<boolean>(settings.modelSettings.rothCModel);
  const [silverServiceVersion, setSilverServiceVersion] = useState<string | null>(settings.modelSettings.silverServiceVersion);
  const shouldShowModelSettings = checkFeatureFlag("model-settings");
  const { data: silverVersions } = useSilverVersionsPublic({ enabled: shouldShowModelSettings });
  const [isAddFieldDrawerOpen, setIsAddFieldDrawerOpen] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const sortedFields = useMemo(() => [...fields].sort((a, b) => (b.size ?? 0) - (a.size ?? 0)), [fields]);
  const lastValidFieldId = useRef<string | null>(settings.location.fieldSelect ?? sortedFields[0]?.id ?? null);
  const guidancePanelRef = useRef<HTMLElement>(null);

  const initialDiesel = settings.variableCosts.machineryDiesel ?? nonCropDefaults?.machineryDiesel ?? null;

  const marketPricePct: number = Form.useWatch("marketPriceTrend", form) ?? settings.forecastControls.marketPriceTrend;
  const costDevPct: number = Form.useWatch("costDevelopment", form) ?? settings.forecastControls.costDevelopment;

  const handleFocus = (key: ProjectSettingsHelpFieldKey) => setFocusedField(key);
  const hasGuidance = focusedField !== null && FIELDS_WITH_GUIDANCE.has(focusedField);

  useEffect(() => {
    if (guidancePanelRef.current) {
      guidancePanelRef.current.scrollTop = 0;
    }
  }, [focusedField]);

  const handleValuesChange = (changedValues: Record<string, unknown>) => {
    const changedKey = Object.keys(changedValues)[0] as ProjectSettingsHelpFieldKey;
    if (changedKey) setFocusedField(changedKey);
  };

  const fieldUnits: Record<ProjectSettingsHelpFieldKey, string | undefined> = {
    marketPriceTrend: "%",
    costDevelopment: "%",
    fieldSelect: undefined,
    machineryDiesel: `${currencySymbol}/liter`,
    climateImpact: undefined,
    rothCModel: undefined,
    silverServiceVersion: undefined,
  };

  const handleSave = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue(true);
      const shownSettings: ProjectSettings = {
        ...settings,
        location: { fieldSelect: settings.location.fieldSelect ?? sortedFields[0]?.id ?? null },
        variableCosts: { machineryDiesel: initialDiesel },
      };
      onSave(
        {
          forecastControls: {
            marketPriceTrend: values.marketPriceTrend ?? 0,
            costDevelopment: values.costDevelopment ?? 0,
          },
          location: {
            fieldSelect: values.fieldSelect ?? null,
          },
          variableCosts: {
            machineryDiesel: values.machineryDiesel ?? null,
          },
          modelSettings: { climateImpact, rothCModel, silverServiceVersion },
        },
        shownSettings
      );
    } catch {
      // Validation errors shown inline
    }
  };

  const handleResetAndClose = () => {
    form.resetFields();
    setClimateImpact(settings.modelSettings.climateImpact);
    setRothCModel(settings.modelSettings.rothCModel);
    setSilverServiceVersion(settings.modelSettings.silverServiceVersion);
    onClose();
  };

  const handleCancel = () => {
    handleResetAndClose();
  };
  const machineryDieselRule = numberRangeRule(
    MACHINERY_DIESEL_LIMITS.min,
    MACHINERY_DIESEL_LIMITS.max,
    t("farmDetailsDrawer.validation.machineryDieselRange", { currency: currencySymbol })
  );

  const footerButtons = [
    <Button key="cancel" type="secondary" onClick={handleCancel}>
      {t("farmDetailsDrawer.cancel")}
    </Button>,
    <Button key="save" type="primary" disabled={hasErrors} onClick={handleSave} dataTestId="project-settings-save">
      {t("farmDetailsDrawer.save")}
    </Button>,
  ];

  const drawerTitle = (
    <div className="project-settings-drawer-header">
      <span className="project-settings-drawer-title">{t("farmDetailsDrawer.title")}</span>
      <Typography variant="paragraph-s" className="project-settings-drawer-description">
        {t("farmDetailsDrawer.description")}
      </Typography>
    </div>
  );

  const stableLabel = t("farmDetailsDrawer.stable");

  // defaults are evaluated on each open but Saved values still take precedence
  useEffect(() => {
    if (!visible) return;

    const currentFieldId = settings.location.fieldSelect ?? sortedFields[0]?.id ?? null;
    form.setFieldsValue({
      marketPriceTrend: settings.forecastControls.marketPriceTrend,
      costDevelopment: settings.forecastControls.costDevelopment,
      fieldSelect: currentFieldId,
      machineryDiesel: initialDiesel,
    });
    lastValidFieldId.current = currentFieldId;
    setClimateImpact(settings.modelSettings.climateImpact);
    setRothCModel(settings.modelSettings.rothCModel);
    setSilverServiceVersion(settings.modelSettings.silverServiceVersion);
  }, [form, settings, visible, initialDiesel, sortedFields]);

  return (
    <>
      <Drawer
        className="project-settings-drawer"
        visible={visible}
        title={drawerTitle}
        footer={footerButtons}
        onClose={handleResetAndClose}
        destroyOnHidden
        width={SOIL_SIGHT_DRAWER_WIDTH}
        dataTestId="project-settings-drawer"
      >
        <div className="project-settings-drawer-body">
          <Form
            form={form}
            className="project-settings-drawer-left"
            initialValues={{
              marketPriceTrend: settings.forecastControls.marketPriceTrend,
              costDevelopment: settings.forecastControls.costDevelopment,
              fieldSelect: settings.location.fieldSelect ?? sortedFields[0]?.id ?? null,
              machineryDiesel: initialDiesel,
            }}
            onValuesChange={handleValuesChange}
            onFieldsChange={(_, allFields) => {
              setHasErrors(allFields.some((field) => (field.errors?.length ?? 0) > 0));
            }}
          >
            {/* ── FORECAST ── */}
            <section className="project-settings-drawer-section-card">
              <div className="project-settings-drawer-section-card-heading">{t("farmDetailsDrawer.sections.forecast")}</div>

              <div
                className="project-settings-drawer-slider-wrapper"
                data-testid="project-settings-market-price-slider"
                onClick={() => handleFocus("marketPriceTrend")}
              >
                <div className="project-settings-drawer-slider-header">
                  <label className="project-settings-drawer-field-label">{t("farmDetailsDrawer.fields.marketPriceTrend.label")}</label>
                  <span key={`market-price-trend-${marketPricePct}`} className={`project-settings-drawer-slider-value ${getMarketPriceClass(marketPricePct)}`}>
                    {formatSignedPercent(marketPricePct)}
                  </span>
                </div>
                <Form.Item name="marketPriceTrend" noStyle>
                  <Slider min={TREND_PCT_LIMITS.min} max={TREND_PCT_LIMITS.max} step={1} tooltip={{ open: false }} />
                </Form.Item>
                <div className="project-settings-drawer-slider-labels">
                  <span>{formatSignedPercent(TREND_PCT_LIMITS.min)}</span>
                  <span>{stableLabel}</span>
                  <span>{formatSignedPercent(TREND_PCT_LIMITS.max)}</span>
                </div>
              </div>

              <div
                className="project-settings-drawer-slider-wrapper"
                data-testid="project-settings-cost-development-slider"
                onClick={() => handleFocus("costDevelopment")}
              >
                <div className="project-settings-drawer-slider-header">
                  <label className="project-settings-drawer-field-label">{t("farmDetailsDrawer.fields.costDevelopment.label")}</label>
                  <span key={`cost-development-${costDevPct}`} className={`project-settings-drawer-slider-value ${getCostDevClass(costDevPct)}`}>
                    {formatSignedPercent(costDevPct)}
                  </span>
                </div>
                <Form.Item name="costDevelopment" noStyle>
                  <Slider min={TREND_PCT_LIMITS.min} max={TREND_PCT_LIMITS.max} step={1} tooltip={{ open: false }} />
                </Form.Item>
                <div className="project-settings-drawer-slider-labels">
                  <span>{formatSignedPercent(TREND_PCT_LIMITS.min)}</span>
                  <span>{stableLabel}</span>
                  <span>{formatSignedPercent(TREND_PCT_LIMITS.max)}</span>
                </div>
              </div>
            </section>

            {/* ── VARIABLE COSTS ── */}
            <section className="project-settings-drawer-section-card">
              <div className="project-settings-drawer-section-card-heading">{t("farmDetailsDrawer.sections.variableCosts")}</div>

              <div className="project-settings-drawer-grid">
                <HelpFieldWrapper className="project-settings-drawer-field-wrapper" fieldKey="machineryDiesel" onFocus={handleFocus}>
                  <InputNumber
                    name="machineryDiesel"
                    dataTestId="project-settings-machinery-diesel"
                    label={t("farmDetailsDrawer.fields.machineryDiesel.label")}
                    suffix={fieldUnits.machineryDiesel}
                    step={0.01}
                    precision={2}
                    initialValue={initialDiesel ?? undefined}
                    placeholder="0.00"
                    validateTrigger="onBlur"
                    rules={[machineryDieselRule]}
                  />
                </HelpFieldWrapper>
              </div>
            </section>

            {/* ── LOCATION ── */}
            <section className="project-settings-drawer-section-card">
              <div className="project-settings-drawer-section-card-heading">{t("farmDetailsDrawer.sections.location")}</div>

              <div
                className="project-settings-drawer-field-group project-settings-drawer-field-wrapper"
                onClick={() => handleFocus("fieldSelect")}
                onFocus={() => handleFocus("fieldSelect")}
              >
                <Select
                  name="fieldSelect"
                  initialValue={settings.location.fieldSelect ?? sortedFields[0]?.id ?? null}
                  className="project-settings-drawer-field-select"
                  popupClassName="project-settings-field-select-dropdown"
                  labelRender={({ value }) => {
                    const field = sortedFields.find((f) => f.id === value);
                    if (!field) return null;
                    return (
                      <div className="project-settings-drawer-field-card-trigger">
                        <FieldMapIcon
                          field={field}
                          className="project-settings-drawer-field-card-icon"
                          fallback={<Icon iconName="tabler/map-pin" size="l" />}
                        />
                        <div className="project-settings-drawer-field-card-info">
                          <div className="project-settings-drawer-field-card-name">{field.name}</div>
                          {field.size > 0 && (
                            <div className="project-settings-drawer-field-card-details">
                              <span className="project-settings-drawer-field-card-area">{field.size} ha</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }}
                  onChange={(val: string) => {
                    if (val === "__add_field__") {
                      form.setFieldValue("fieldSelect", lastValidFieldId.current);
                      setIsAddFieldDrawerOpen(true);
                    } else {
                      lastValidFieldId.current = val;
                    }
                  }}
                >
                  {sortedFields.map((field) => (
                    <Option key={field.id} value={field.id}>
                      <div className="project-settings-drawer-field-option">
                        <FieldMapIcon
                          field={field}
                          className="project-settings-drawer-field-card-icon"
                          fallback={<Icon iconName="tabler/map-pin" size="l" />}
                        />
                        <div className="project-settings-drawer-field-card-info">
                          <div className="project-settings-drawer-field-card-name">{field.name}</div>
                          {field.size > 0 && (
                            <div className="project-settings-drawer-field-card-details">
                              <span className="project-settings-drawer-field-card-area">{field.size} ha</span>
                            </div>
                          )}
                        </div>
                        <div className="project-settings-drawer-field-card-chevron">
                          <Icon iconName="tabler/chevron-right" size="s" />
                        </div>
                      </div>
                    </Option>
                  ))}
                  <Option value="__add_field__" className="project-settings-drawer-add-field-option">
                    <div className="project-settings-drawer-add-field-content">
                      <div className="project-settings-drawer-add-field-icon">
                        <Icon iconName="klim/plus" size="s" />
                      </div>
                      <span className="project-settings-drawer-add-field-label">{t("farmDetailsDrawer.addField")}</span>
                    </div>
                  </Option>
                </Select>
              </div>
            </section>

            {/* ── MODEL SETTINGS ── */}
            {shouldShowModelSettings && (
              <section className="project-settings-drawer-section-card">
                <div className="project-settings-drawer-section-card-heading">{t("farmDetailsDrawer.sections.modelSettings")}</div>

                <div
                  className={`project-settings-drawer-field-group ${focusedField === "climateImpact" ? "is-focused" : ""}`}
                  onClick={() => handleFocus("climateImpact")}
                >
                  <label className="project-settings-drawer-field-label">{t("farmDetailsDrawer.fields.climateImpact.label")}</label>
                  <KlimRadioGroup
                    options={[
                      { value: "enabled", label: t("farmDetailsDrawer.enabled") },
                      { value: "disabled", label: t("farmDetailsDrawer.disabled") },
                    ]}
                    value={climateImpact ? "enabled" : "disabled"}
                    onChange={(e: RadioChangeEvent) => setClimateImpact(e.target.value === "enabled")}
                    type="tertiary"
                    size="large"
                    optionType="button"
                    buttonStyle="solid"
                  />
                </div>

                <div
                  className={`project-settings-drawer-field-group ${focusedField === "rothCModel" ? "is-focused" : ""}`}
                  onClick={() => handleFocus("rothCModel")}
                >
                  <label className="project-settings-drawer-field-label">{t("farmDetailsDrawer.fields.rothCModel.label")}</label>
                  <KlimRadioGroup
                    options={[
                      { value: "enabled", label: t("farmDetailsDrawer.enabled") },
                      { value: "disabled", label: t("farmDetailsDrawer.disabled") },
                    ]}
                    value={rothCModel ? "enabled" : "disabled"}
                    onChange={(e: RadioChangeEvent) => setRothCModel(e.target.value === "enabled")}
                    type="tertiary"
                    size="large"
                    optionType="button"
                    buttonStyle="solid"
                  />
                </div>

                <div
                  className={`project-settings-drawer-field-group ${focusedField === "silverServiceVersion" ? "is-focused" : ""}`}
                  onClick={() => handleFocus("silverServiceVersion")}
                >
                  <label className="project-settings-drawer-field-label">{t("farmDetailsDrawer.fields.silverServiceVersion.label")}</label>
                  <Select
                    value={silverServiceVersion ?? DEFAULT_VERSION}
                    onChange={(value: string) => setSilverServiceVersion(value === DEFAULT_VERSION ? null : value)}
                    style={{ width: "100%" }}
                    dataTestId="project-settings-silver-service-version"
                  >
                    <Option value={DEFAULT_VERSION}>{t("farmDetailsDrawer.fields.silverServiceVersion.default")}</Option>
                    {(silverVersions ?? []).map((v) => (
                      <Option key={v.version} value={v.version}>
                        {versionOptionLabel(v)}
                      </Option>
                    ))}
                  </Select>
                </div>
              </section>
            )}
          </Form>

          <aside ref={guidancePanelRef} className="project-settings-drawer-right">
            {focusedField ? (
              <GuidancePanel
                key={focusedField}
                fieldLabel={t(`farmDetailsDrawer.fields.${focusedField}.label`)}
                description={hasGuidance ? t(`refineInputs.guidance.${focusedField}.description`) : ""}
                source={hasGuidance ? t(`refineInputs.guidance.${focusedField}.source`) : ""}
                whatIsItLabel={t("farmDetailsDrawer.guidance.whatIsIt")}
                sourceLabel={t("farmDetailsDrawer.guidance.source")}
                unit={fieldUnits[focusedField] ? `(${fieldUnits[focusedField]})` : undefined}
              />
            ) : (
              <Typography variant="paragraph-s" className="project-settings-drawer-guidance-prompt">
                {t("farmDetailsDrawer.guidance.defaultPrompt")}
              </Typography>
            )}
          </aside>
        </div>
      </Drawer>

      <Drawer
        className="project-settings-add-field-drawer"
        visible={isAddFieldDrawerOpen}
        title={t("farmDetailsDrawer.addFieldDrawer.title")}
        onClose={() => setIsAddFieldDrawerOpen(false)}
        dataTestId="add-field-drawer"
      >
        <FieldUpload
          onFinish={() => {
            setIsAddFieldDrawerOpen(false);
            onFieldAdded?.();
          }}
        />
      </Drawer>
    </>
  );
};

export default ProjectSettingsDrawer;
